import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sparkles, Loader2, CheckCircle2, AlertTriangle, XCircle,
  RefreshCw, ChevronDown, X, Type, Hash, Quote, Pen, BookOpen, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// D50 / BUG-04 — split extracted_text around the active issue's text_range so
// React can render a <mark> highlight in place. Falls back to a plain
// substring search if the LLM didn't provide a range (defensive — the
// backend normalizer already backfills text_range, but old reviews might not
// have it).
function renderExtractedWithHighlight(text, activeIssue) {
  if (!activeIssue) return text;
  let start = -1;
  let end = -1;
  if (activeIssue.text_range && Array.isArray(activeIssue.text_range)) {
    [start, end] = activeIssue.text_range;
  }
  if (start < 0 && activeIssue.original) {
    const idx = text.indexOf(activeIssue.original);
    if (idx >= 0) { start = idx; end = idx + activeIssue.original.length; }
  }
  if (start < 0 || end <= start || end > text.length) return text;
  return (
    <>
      {text.slice(0, start)}
      <mark
        data-ai-mark="active"
        className="bg-gold/35 text-foreground rounded-sm px-0.5 ring-1 ring-gold/60"
        data-testid={`ai-extracted-highlight-${activeIssue.id}`}
      >
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

// D50 / BUG-04 — image-bbox preview. Renders the source image (clamped to
// ~480×320) with an SVG overlay drawing a red rectangle around the active
// issue's bbox. Falls back to "Open original" if no bbox info is available.
function ImageBboxPreview({ file, activeIssue, imageWidth, imageHeight }) {
  const [naturalW, setNaturalW] = useState(imageWidth || null);
  const [naturalH, setNaturalH] = useState(imageHeight || null);
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(480);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry?.contentRect?.width) setContainerW(Math.min(640, entry.contentRect.width));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (!file?.url) return null;
  const bbox = activeIssue?.bbox;
  const scale = naturalW ? (containerW / naturalW) : 1;
  const displayH = naturalH ? naturalH * scale : null;

  return (
    <div ref={containerRef} className="space-y-1" data-testid="ai-image-preview">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Source image</p>
      <div className="relative inline-block rounded-md border border-border/20 overflow-hidden bg-foreground/[0.02]" style={{ maxWidth: '100%' }}>
        <img
          src={file.url}
          alt={file.filename || 'submission'}
          style={{ width: containerW, height: displayH || 'auto', display: 'block' }}
          onLoad={(e) => {
            if (!naturalW) setNaturalW(e.target.naturalWidth);
            if (!naturalH) setNaturalH(e.target.naturalHeight);
          }}
          data-testid="ai-image-preview-img"
        />
        {bbox && naturalW && naturalH && (
          <svg
            viewBox={`0 0 ${naturalW} ${naturalH}`}
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: 0, width: containerW, height: displayH, pointerEvents: 'none' }}
            data-testid={`ai-image-bbox-${activeIssue.id}`}
          >
            <rect
              x={bbox[0]} y={bbox[1]} width={bbox[2]} height={bbox[3]}
              fill="none"
              stroke="rgb(220, 38, 38)"
              strokeWidth={Math.max(2, naturalW / 240)}
              strokeDasharray={`${Math.max(6, naturalW / 80)},${Math.max(4, naturalW / 120)}`}
            />
          </svg>
        )}
      </div>
      {!bbox && activeIssue && (
        <p className="text-[10px] text-muted-foreground/50 italic">
          No bbox info for this suggestion — text highlight shown below.
        </p>
      )}
    </div>
  );
}

// D51 — Video preview with scrub-to-timestamp. When the active issue has a
// `timestamp_s` and `source: 'frame_N'`, the embedded <video> element seeks
// to that timestamp. The frame timestamp chip is also rendered above so the
// user can preview frames without an active issue.
function VideoTimestampPreview({ file, activeIssue, videoMeta }) {
  const videoRef = useRef(null);

  // When activeIssue changes, seek the video.
  useEffect(() => {
    if (!activeIssue || !videoRef.current) return;
    const ts = activeIssue.timestamp_s;
    if (typeof ts !== 'number' || ts < 0) return;
    try {
      videoRef.current.currentTime = ts;
      // Pause so the user sees the frame the AI flagged.
      try { videoRef.current.pause(); } catch {}
    } catch {}
  }, [activeIssue]);

  if (!file?.url) return null;
  const frames = videoMeta?.frames || [];
  return (
    <div className="space-y-2" data-testid="ai-video-preview">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Source video</p>
      <video
        ref={videoRef}
        src={file.url}
        controls
        preload="metadata"
        className="max-w-full rounded-md border border-border/20 bg-black/40"
        style={{ maxHeight: 360 }}
        data-testid="ai-video-preview-el"
      />
      {frames.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="ai-video-frame-chips">
          {frames.map((fr) => (
            <button
              key={fr.index}
              type="button"
              onClick={() => {
                if (videoRef.current && typeof fr.timestamp_s === 'number') {
                  try {
                    videoRef.current.currentTime = fr.timestamp_s;
                    videoRef.current.pause();
                  } catch {}
                }
              }}
              className={`text-[10px] rounded px-1.5 py-0.5 border transition-colors ${
                activeIssue?.timestamp_s === fr.timestamp_s
                  ? 'bg-gold/20 text-gold border-gold/40'
                  : 'bg-foreground/[0.04] text-muted-foreground/80 border-border/30 hover:bg-foreground/[0.08]'
              }`}
              data-testid={`ai-video-frame-chip-${fr.index}`}
              title={`Frame ${fr.index} at ${fr.timestamp_s?.toFixed?.(1) || '?'}s${fr.has_text ? ' (has text)' : ' (no text)'}`}
            >
              <span className="font-mono">{fr.timestamp_s?.toFixed?.(1) || '?'}s</span>
              {fr.has_text ? '' : ' ·'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const DISMISS_REASONS = [
  { value: 'brand_name',     label: 'Brand name (workspace-wide)',  desc: 'Add to dismiss list — never flag again' },
  { value: 'false_positive', label: 'False positive (workspace-wide)', desc: 'Add to dismiss list — never flag again' },
  { value: 'intentional',    label: 'Intentional (this submission only)', desc: 'Local — only dismissed here' },
];

const ISSUE_ICON = {
  spelling: Pen,
  grammar: BookOpen,
  punctuation: Quote,
  capitalization: Type,
  typo: Hash,
};

const SEVERITY_COLOR = {
  high:   'text-red-300 bg-red-500/10 border-red-500/30',
  medium: 'text-amber-300 bg-amber-500/10 border-amber-500/30',
  low:    'text-zinc-300 bg-zinc-500/10 border-zinc-500/30',
};

// ───────── Badge that overlays a thumbnail ─────────
export function AiReviewBadge({ aiReview, onClick, onRetry }) {
  // No badge when the file hasn't been touched by AI review (toggle off, or
  // pre-existing file uploaded before the feature shipped).
  if (!aiReview) return null;
  const status = aiReview.status;
  if (status === 'pending') {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800/80 text-[9px] text-zinc-300 border border-zinc-700/50"
        data-testid="ai-review-badge-pending"
      >
        <Loader2 className="h-2.5 w-2.5 animate-spin" /> Reviewing…
      </span>
    );
  }
  if (status === 'skipped') {
    const reasonLabel = {
      video: 'Skipped (video)',
      design_file: 'Skipped (design)',
      office_doc: 'Skipped (doc)',
      no_api_key: 'AI unavailable',
      too_small: 'Too small to review',
      unsupported: 'Skipped',
    }[aiReview.reason] || 'Skipped';
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800/80 text-[9px] text-zinc-400 border border-zinc-700/50"
        data-testid="ai-review-badge-skipped"
        title={reasonLabel}
      >
        <Sparkles className="h-2.5 w-2.5" /> {reasonLabel}
      </span>
    );
  }
  if (status === 'error' || status === 'failed') {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onRetry?.(); }}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-900/40 text-[9px] text-red-300 border border-red-700/40 hover:bg-red-900/60"
        title={aiReview.error_message || 'AI review failed — click to retry'}
        data-testid="ai-review-badge-error"
      >
        <XCircle className="h-2.5 w-2.5" /> Review failed
        <RefreshCw className="h-2.5 w-2.5 ml-0.5" />
      </button>
    );
  }
  if (status === 'completed') {
    const active = (aiReview.issues || []).filter((i) => !i.dismissed);
    if (active.length === 0) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); onClick?.(); }}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-900/30 text-[9px] text-emerald-300 border border-emerald-700/40 hover:bg-emerald-900/50"
          data-testid="ai-review-badge-clean"
        >
          <CheckCircle2 className="h-2.5 w-2.5" /> Looks good
        </button>
      );
    }
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-900/40 text-[9px] text-amber-300 border border-amber-700/40 hover:bg-amber-900/60"
        data-testid="ai-review-badge-issues"
      >
        <AlertTriangle className="h-2.5 w-2.5" />
        {active.length} suggestion{active.length === 1 ? '' : 's'}
      </button>
    );
  }
  return null;
}

// ───────── Modal showing extracted text + issues with dismiss UI ─────────
export function AiReviewIssuesModal({
  open, onOpenChange, file, workspaceId, taskId, submissionId, onChanged,
}) {
  const aiReview = file?.ai_review;
  const [busyIssue, setBusyIssue] = useState(null); // {id, reason}
  const [reasonOpen, setReasonOpen] = useState(null); // issue id
  const [bulkOpen, setBulkOpen] = useState(false);

  const issues = aiReview?.issues || [];
  const activeIssues = issues.filter((i) => !i.dismissed);

  // D50 / BUG-04 — locator state. activeIssueId is the issue whose
  // text_range/bbox is currently highlighted in the source preview. Clicking
  // a suggestion sets this; clicking again clears it.
  const [activeIssueId, setActiveIssueId] = useState(null);
  const extractedTextRef = useRef(null);
  const activeIssue = useMemo(() => issues.find((it) => it.id === activeIssueId) || null, [issues, activeIssueId]);

  // Scroll/highlight the active text_range inside the extracted-text panel.
  // Done via a small auto-scroll on click — the <pre> renders a mark span for
  // the matching slice.
  useEffect(() => {
    if (!activeIssue || !extractedTextRef.current || !activeIssue.text_range) return;
    const mark = extractedTextRef.current.querySelector('[data-ai-mark="active"]');
    if (mark && mark.scrollIntoView) {
      try { mark.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
    }
  }, [activeIssueId, activeIssue]);

  const dismiss = async (issueId, reason) => {
    setBusyIssue({ id: issueId, reason });
    try {
      const r = await fetch(
        `${API}/api/tasks/${workspaceId}/${taskId}/submissions/${submissionId}/files/${file.id}/ai-review/dismiss`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
          },
          body: JSON.stringify({ issue_id: issueId, reason }),
        }
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${r.status}`);
      }
      onChanged?.();
      setReasonOpen(null);
      toast.success(
        reason === 'intentional'
          ? 'Dismissed for this submission'
          : 'Dismissed and added to workspace dismiss list'
      );
    } catch (e) {
      toast.error(`Dismiss failed: ${e.message || 'unknown'}`);
    } finally {
      setBusyIssue(null);
    }
  };

  const dismissAll = async (reason) => {
    setBulkOpen(false);
    for (const it of activeIssues) {
      // sequential to keep workspace dismiss list consistent
      // eslint-disable-next-line no-await-in-loop
      await dismiss(it.id, reason);
    }
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[640px] max-h-[85vh] bg-card border-border/40 overflow-hidden flex flex-col"
        data-testid="ai-review-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-[14px] font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-gold" /> AI Content Review
            <span className="text-[11px] text-muted-foreground/60 font-normal">— {file.filename}</span>
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            AI suggestions are not always correct. Brand names, intentional capitalization, and hashtags may be flagged.
            Dismissed flags reduce noise over time.
          </p>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6 space-y-4 flex-1">
          {/* D50 — locator panel. For image files we render a small preview
              showing the source image with a red bbox around the active
              suggestion. For PDFs/text posts we use the extracted-text
              highlight below. */}
          {file?.mime_type?.startsWith('image/') && file?.url && (
            <ImageBboxPreview
              file={file}
              activeIssue={activeIssue}
              imageWidth={aiReview?.image_width}
              imageHeight={aiReview?.image_height}
            />
          )}

          {/* D51 — video preview with scrub-to-timestamp + frame chips. */}
          {file?.mime_type?.startsWith('video/') && file?.url && (
            <VideoTimestampPreview
              file={file}
              activeIssue={activeIssue}
              videoMeta={aiReview?.video_meta}
            />
          )}

          {/* Extracted text — D50 — renders the active issue's text_range as
              a highlighted mark and auto-scrolls to it. */}
          {aiReview?.extracted_text && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Extracted text</p>
              <pre
                ref={extractedTextRef}
                className="text-[12px] text-foreground/90 bg-foreground/[0.03] rounded-md px-3 py-2 max-h-[160px] overflow-y-auto whitespace-pre-wrap"
                data-testid="ai-review-extracted-text"
              >
                {renderExtractedWithHighlight(aiReview.extracted_text, activeIssue)}
              </pre>
            </div>
          )}

          {/* Issues */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                {activeIssues.length} active suggestion{activeIssues.length === 1 ? '' : 's'}
                {issues.length > activeIssues.length && (
                  <span className="ml-1 text-muted-foreground/50">({issues.length - activeIssues.length} dismissed)</span>
                )}
              </p>
              {activeIssues.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setBulkOpen((o) => !o)}
                    className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/70 hover:text-gold border border-border/30 rounded px-2 py-0.5"
                    data-testid="ai-dismiss-all-btn"
                  >
                    Dismiss all <ChevronDown className="h-3 w-3" />
                  </button>
                  {bulkOpen && (
                    <div
                      className="absolute right-0 top-full mt-1 w-[260px] rounded-md border border-border/40 bg-card shadow-lg z-40 pointer-events-auto"
                      onMouseLeave={() => setBulkOpen(false)}
                    >
                      {DISMISS_REASONS.map((r) => (
                        <button
                          key={r.value}
                          onClick={() => dismissAll(r.value)}
                          className="w-full text-left px-3 py-2 text-[11px] hover:bg-foreground/[0.04] border-b border-border/20 last:border-b-0"
                          data-testid={`ai-dismiss-all-${r.value}`}
                        >
                          <div className="font-medium">{r.label}</div>
                          <div className="text-muted-foreground/60 text-[10px]">{r.desc}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {issues.length === 0 && (
              <div className="rounded-md border border-emerald-700/30 bg-emerald-900/15 px-3 py-3 text-[12px] text-emerald-300 flex items-center gap-2" data-testid="ai-no-issues">
                <CheckCircle2 className="h-4 w-4" />
                No issues found. Looks good!
              </div>
            )}

            <div className="space-y-2">
              {issues.map((it) => {
                const Icon = ISSUE_ICON[it.type] || Pen;
                return (
                  <div
                    key={it.id}
                    className={
                      'rounded-md border px-3 py-2 transition-all ' +
                      (it.dismissed ? 'opacity-50 border-border/20 bg-foreground/[0.02]' : (
                        activeIssueId === it.id
                          ? 'border-gold/60 bg-gold/[0.06] ring-1 ring-gold/20'
                          : 'border-border/30 bg-foreground/[0.03] hover:border-border/50'
                      ))
                    }
                    data-testid={`ai-issue-${it.id}`}
                  >
                    <div className="flex items-center gap-2 text-[10px] mb-1">
                      <Icon className="h-3 w-3 text-muted-foreground/70" />
                      <span className="capitalize text-muted-foreground/80">{it.type}</span>
                      <Badge className={'text-[9px] px-1.5 py-0 ' + (SEVERITY_COLOR[it.severity] || SEVERITY_COLOR.medium)}>
                        {it.severity}
                      </Badge>
                      {/* Phase 3A Feature 2 — surface the issue's origin
                          (e.g. "Frame 2" or "Captions") when present. Video
                          reviews tag every issue with `source`; image/PDF
                          reviews don't, so the badge is conditional. */}
                      {it.source && (
                        <Badge
                          className="text-[9px] px-1.5 py-0 bg-blue-500/15 text-blue-300 border-blue-500/30"
                          data-testid={`ai-issue-source-${it.id}`}
                        >
                          {String(it.source).replace('_', ' ').replace('frame ', 'Frame ').replace('captions', 'Captions').replace('transcript', 'Transcript')}
                        </Badge>
                      )}
                      {/* D50 — locator chips: page number for PDFs, "Locate"
                          button when text_range or bbox is available. */}
                      {it.page_number != null && (
                        <Badge
                          className="text-[9px] px-1.5 py-0 bg-violet-500/15 text-violet-300 border-violet-500/30"
                          data-testid={`ai-issue-page-${it.id}`}
                        >
                          Page {it.page_number}
                        </Badge>
                      )}
                      {/* D51 — timestamp chip for video frame issues. */}
                      {typeof it.timestamp_s === 'number' && (
                        <Badge
                          className="text-[9px] px-1.5 py-0 bg-cyan-500/15 text-cyan-300 border-cyan-500/30 font-mono"
                          data-testid={`ai-issue-timestamp-${it.id}`}
                        >
                          {it.timestamp_s.toFixed(1)}s
                        </Badge>
                      )}
                      {(it.text_range || it.bbox || typeof it.timestamp_s === 'number') && !it.dismissed && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveIssueId(activeIssueId === it.id ? null : it.id);
                          }}
                          className={`inline-flex items-center gap-1 text-[10px] rounded px-1.5 py-0.5 border transition-colors ${
                            activeIssueId === it.id
                              ? 'bg-gold/20 text-gold border-gold/40'
                              : 'bg-gold/5 text-gold/80 border-gold/20 hover:bg-gold/15'
                          }`}
                          data-testid={`ai-locate-btn-${it.id}`}
                          title={activeIssueId === it.id ? 'Hide highlight' : 'Show where in source'}
                        >
                          <MapPin className="h-2.5 w-2.5" />
                          {activeIssueId === it.id ? 'Hide' : 'Locate'}
                        </button>
                      )}
                      {it.location_hint && (
                        <span className="text-muted-foreground/50 truncate">· {it.location_hint}</span>
                      )}
                      <div className="ml-auto">
                        {it.dismissed ? (
                          <span className="text-[10px] text-muted-foreground/60">
                            Dismissed{it.dismissed_reason ? ` (${it.dismissed_reason.replace('_', ' ')})` : ''}
                          </span>
                        ) : (
                          <div className="relative">
                            <button
                              onClick={() => setReasonOpen((o) => (o === it.id ? null : it.id))}
                              className="text-[10px] text-muted-foreground/70 hover:text-gold border border-border/30 rounded px-2 py-0.5 flex items-center gap-1"
                              disabled={busyIssue?.id === it.id}
                              data-testid={`ai-dismiss-btn-${it.id}`}
                            >
                              {busyIssue?.id === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Dismiss'}
                              <ChevronDown className="h-3 w-3" />
                            </button>
                            {reasonOpen === it.id && (
                              <div
                                className="absolute right-0 top-full mt-1 w-[260px] rounded-md border border-border/40 bg-card shadow-lg z-40 pointer-events-auto"
                                onMouseLeave={() => setReasonOpen(null)}
                              >
                                {DISMISS_REASONS.map((r) => (
                                  <button
                                    key={r.value}
                                    onClick={() => dismiss(it.id, r.value)}
                                    className="w-full text-left px-3 py-2 text-[11px] hover:bg-foreground/[0.04] border-b border-border/20 last:border-b-0"
                                    data-testid={`ai-dismiss-${it.id}-${r.value}`}
                                  >
                                    <div className="font-medium">{r.label}</div>
                                    <div className="text-muted-foreground/60 text-[10px]">{r.desc}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-[12px] text-foreground/90 break-words">
                      <span className="text-red-300/90 line-through mr-2">{it.original}</span>
                      {it.suggestion && (
                        <>
                          <span className="text-muted-foreground/40">→</span>
                          <span className="text-emerald-300/90 ml-2">{it.suggestion}</span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-border/20">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="ai-review-close">
            <X className="h-3.5 w-3.5 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───────── Hook to poll while any file is pending ─────────
// D88 — fixes for the "infinite 4-second poll" bug:
//   • Hard cap on total attempts so a stuck `pending` can never poll forever.
//   • Linear back-off (4s → 8s → 12s) so the request rate drops if the
//     review legitimately takes longer than expected.
//   • Surgical update path via `onMerge` — refreshes ONLY the ai_review
//     fields without rebuilding the whole submissions tree, so the comment
//     composer's textarea is never remounted and the user's draft survives.
//   • Falls back to onRefresh() only if onMerge isn't provided.
export function useAiReviewPolling({ submissions, onRefresh, onMerge, intervalMs = 4000, maxAttempts = 60 }) {
  const hasPending = useMemo(() => {
    if (!submissions) return false;
    for (const s of submissions) {
      for (const f of (s.files || [])) {
        if (f.type !== 'upload') continue;
        if (f.ai_review?.status === 'pending') return true;
      }
    }
    return false;
  }, [submissions]);

  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!hasPending) {
      attemptsRef.current = 0;
      return;
    }
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      if (attemptsRef.current > maxAttempts) {
        // Bounded — give up rather than poll forever. The backend
        // startup-recovery sweep will eventually flip any truly-stuck
        // pending to 'failed' and the next manual refresh will pick it up.
        return;
      }
      try {
        if (onMerge) {
          await onMerge();
        } else if (onRefresh) {
          await onRefresh();
        }
      } catch (_) {}
      // Linear back-off — first ~10 polls at base interval, then 2x, then 3x.
      const factor = attemptsRef.current < 10 ? 1 : attemptsRef.current < 25 ? 2 : 3;
      timer = setTimeout(tick, intervalMs * factor);
    };
    timer = setTimeout(tick, intervalMs);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // We intentionally do NOT include onMerge/onRefresh in deps — they're
    // stable refs from the parent and including them would restart the
    // timer chain on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPending, intervalMs, maxAttempts]);

  return { hasPending };
}
